import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl, errMsg } from "../lib/api";
import { currency, QUOTE_TYPES } from "../lib/constants";
import GoogleRoofDesigner from "../components/GoogleRoofDesigner";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { ActionsMenu } from "../components/ActionsMenu";
import { useProposalPdfs } from "../hooks/useProposalPdfs";
import { ProposalPdfProgress } from "../components/ProposalPdfProgress";
import { SigningEmailStatus } from "../components/SigningEmailStatus";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const PANEL_MM = { w: 1762, h: 1134 };


function SearchSelect({ value, onChange, options, placeholder, getLabel, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const sel = options.find((o) => o.id === value);
  const term = q.trim().toLowerCase();
  const filtered = term ? options.filter((o) => getLabel(o).toLowerCase().includes(term)) : options;
  return (
    <div className="relative min-w-0 w-full" ref={ref} data-testid={testid}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
        className="w-full min-w-0 mt-1 px-3 py-2 rounded-md border border-input bg-background text-left flex items-center justify-between disabled:opacity-60">
        <span className={`min-w-0 truncate ${sel ? "" : "text-muted-foreground"}`}>{sel ? getLabel(sel) : placeholder}</span>
        <Icons.ChevronsUpDown size={15} className="text-muted-foreground shrink-0 ml-2" />
      </button>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-card border border-border rounded-md shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border bg-card">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setOpen(false); } }}
              className="w-full px-2 py-1.5 rounded border border-input bg-background text-sm" data-testid={`${testid}-search`} />
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button type="button" onClick={() => { onChange(""); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-muted">— None —</button>
            {filtered.map((o) => (
              <button key={o.id} type="button" onClick={() => { onChange(o.id); setOpen(false); setQ(""); }} data-testid={`${testid}-opt`}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${o.id === value ? "bg-primary/10 font-semibold" : ""}`}>{getLabel(o)}</button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export const roofImgStyle = (view) => {
  const v = view || {};
  return { transform: `translate(${v.x || 0}%, ${v.y || 0}%) scale(${v.zoom || 1})`, transformOrigin: "center center" };
};

function RoofDesigner({ imageId, layout, onChange, view, onViewChange }) {
  const [panels, setPanels] = useState(layout?.panels || []);
  const [scale, setScale] = useState(layout?.scale || 70);
  const boxRef = useRef();
  const drag = useRef(null);
  const pan = useRef(null);
  const v = view || { zoom: 1, x: 0, y: 0 };
  const setView = (patch) => onViewChange({ ...v, ...patch });
  const zoomBy = (d) => setView({ zoom: Math.min(4, Math.max(0.5, +((v.zoom || 1) + d).toFixed(2))) });

  useEffect(() => { setPanels(layout?.panels || []); setScale(layout?.scale || 70); }, [imageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (p, s) => onChange({ panels: p, scale: s });
  const addPanel = () => { const np = [...panels, { x: 40, y: 40, rot: 0 }]; setPanels(np); commit(np, scale); };
  const removePanel = (i) => { const np = panels.filter((_, idx) => idx !== i); setPanels(np); commit(np, scale); };
  const rotate = (i) => { const np = panels.map((p, idx) => idx === i ? { ...p, rot: p.rot ? 0 : 90 } : p); setPanels(np); commit(np, scale); };

  const onDown = (e, i) => { e.stopPropagation(); const rect = boxRef.current.getBoundingClientRect();
    drag.current = { i, ox: e.clientX - rect.left - panels[i].x, oy: e.clientY - rect.top - panels[i].y }; };
  const onMove = useCallback((e) => {
    if (pan.current) {
      const rect = boxRef.current.getBoundingClientRect();
      const dx = ((e.clientX - pan.current.sx) / rect.width) * 100, dy = ((e.clientY - pan.current.sy) / rect.height) * 100;
      onViewChange({ ...pan.current.v, x: +(pan.current.v.x + dx).toFixed(2), y: +(pan.current.v.y + dy).toFixed(2) });
      return;
    }
    if (!drag.current) return; const rect = boxRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - drag.current.ox, y = e.clientY - rect.top - drag.current.oy;
    setPanels((ps) => ps.map((p, idx) => idx === drag.current.i ? { ...p, x: Math.max(0, x), y: Math.max(0, y) } : p));
  }, [onViewChange]);
  const onUp = () => { if (drag.current) { commit(panels, scale); drag.current = null; } pan.current = null; };
  const startPan = (e) => { if (!imageId) return; pan.current = { sx: e.clientX, sy: e.clientY, v: { zoom: v.zoom || 1, x: v.x || 0, y: v.y || 0 } }; };

  const pw = scale, ph = scale * (PANEL_MM.h / PANEL_MM.w);

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <button type="button" onClick={addPanel} data-testid="add-panel"
          className="text-sm px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground font-semibold flex items-center gap-1.5"><Icons.Plus size={14} /> Add Panel</button>
        <span className="text-sm text-muted-foreground">{panels.length} panels placed</span>
        <div className="flex items-center gap-1 ml-2" data-testid="roof-zoom-controls">
          <button type="button" onClick={() => zoomBy(-0.1)} data-testid="roof-zoom-out" title="Zoom out" className="p-1.5 rounded-md border border-border hover:bg-muted"><Icons.ZoomOut size={14} /></button>
          <span className="text-xs w-12 text-center tabular-nums" data-testid="roof-zoom-level">{Math.round((v.zoom || 1) * 100)}%</span>
          <button type="button" onClick={() => zoomBy(0.1)} data-testid="roof-zoom-in" title="Zoom in" className="p-1.5 rounded-md border border-border hover:bg-muted"><Icons.ZoomIn size={14} /></button>
          <button type="button" onClick={() => onViewChange({ zoom: 1, x: 0, y: 0 })} data-testid="roof-zoom-reset" title="Reset zoom & position" className="p-1.5 rounded-md border border-border hover:bg-muted"><Icons.Maximize size={14} /></button>
          <span className="text-[11px] text-muted-foreground ml-1 hidden sm:inline">drag image to reposition</span>
        </div>
        <label className="text-xs text-muted-foreground ml-auto">Scale</label>
        <input type="range" min="30" max="140" value={scale} onChange={(e) => { const s = +e.target.value; setScale(s); commit(panels, s); }} />
      </div>
      <div ref={boxRef} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} onMouseDown={startPan}
        className="relative w-full h-[420px] rounded-md border border-border overflow-hidden bg-muted select-none cursor-grab active:cursor-grabbing" data-testid="roof-canvas">
        {imageId ? <img src={fileUrl(imageId)} alt="roof" style={roofImgStyle(v)} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
          : <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">Upload a roof image to begin placement</div>}
        {panels.map((p, i) => (
          <div key={i} onMouseDown={(e) => onDown(e, i)}
            style={{ left: p.x, top: p.y, width: p.rot ? ph : pw, height: p.rot ? pw : ph }}
            className="absolute bg-secondary/70 border-2 border-secondary rounded-sm cursor-move group">
            <div className="absolute inset-0.5 grid grid-cols-3 gap-px opacity-40">
              {Array.from({ length: 6 }).map((_, k) => <div key={k} className="bg-white/40" />)}
            </div>
            <div className="absolute -top-6 left-0 hidden group-hover:flex gap-1">
              <button type="button" onClick={(e) => { e.stopPropagation(); rotate(i); }} className="bg-card border border-border rounded p-0.5"><Icons.RotateCw size={12} /></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); removePanel(i); }} className="bg-card border border-border rounded p-0.5 text-destructive"><Icons.X size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const expiryDate = (days = 15) => {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Melbourne", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type).value;
  const d = new Date(`${get("year")}-${get("month")}-${get("day")}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + Number(days));
  return d.toISOString().slice(0, 10);
};
const newOption = (label, days = 15) => ({
  expires_on: expiryDate(days),
  id: `opt${Date.now()}${Math.floor(Math.random() * 1000)}`,
  label: label || "System Option", quote_type: "pv",
  panel_product_id: "", panel_qty: 0, inverter_product_id: "", inverter_qty: 1,
  battery_product_id: "", battery_qty: 1, out_of_pocket: 0, daily_consumption: 20,
  strategic_discount: 0, deposit: 0, plenti_enabled: false, plenti_term_months: 12,
  finance_provider: "", finance_amount: 0, finance_term_months: 12,
  recommended: false, notes: "", extras: [],
});

const initForm = {
  lead_id: "", annual_bill: "", roof_image_id: null, roof_layout: null, notes: "",
  options: [{ ...newOption("Solar Only") }],
};

export default function QuoteBuilder() {
  const { user } = useAuth();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...initForm, lead_id: sp.get("lead") || "" });
  const [active, setActive] = useState(0);
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [optionCalcs, setOptionCalcs] = useState([]);
  const [saved, setSaved] = useState(null);
  const [savedForm, setSavedForm] = useState(null);
  const proposalPdfs = useProposalPdfs(saved?.id, saved?.updated_at);
  const [settings, setSettings] = useState(null);
  const [roofMode, setRoofMode] = useState("satellite");
  const roofRef = useRef();

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (!id) setForm((f) => ({ ...f, options: f.options.map((o) => ({ ...o, expires_on: expiryDate(r.data.quote_expiry?.default_days || 15) })) }));
    }).catch(() => {});
    if (id) api.get(`/quotes/${id}/full`).then((r) => {
      const q = r.data.quote;
      const opts = (r.data.options && r.data.options.length) ? r.data.options : [{ ...newOption("System Option") }];
      const withRoof = opts.map((o) => (o.roof_image_id || o.roof_layout) ? o : { ...o, roof_image_id: q.roof_image_id || null, roof_layout: q.roof_layout || null });
      const loadedForm = { lead_id: q.lead_id, annual_bill: q.annual_bill ?? "", roof_image_id: q.roof_image_id, roof_layout: q.roof_layout, notes: q.notes || "", options: withRoof };
      setForm(loadedForm); setSavedForm(JSON.stringify(loadedForm));
      setSaved(q); setOptionCalcs(r.data.option_calcs || []);
      const r0 = withRoof[0] || {};
      setRoofMode(r0.roof_layout?.center ? "satellite" : (r0.roof_image_id ? "upload" : "satellite"));
    });
  }, [id]);

  const panels = products.filter((p) => p.category === "panel" && p.active !== false);
  const inverters = products.filter((p) => p.category === "inverter" && p.active !== false);
  const batteries = products.filter((p) => p.category === "battery" && p.active !== false);
  const plentiTerms = settings?.plenti?.terms || [];

  const opt = form.options[active] || form.options[0];
  const setOpt = (k, v) => setForm((f) => {
    const options = f.options.map((o, i) => i === active ? { ...o, [k]: v } : o);
    return { ...f, options };
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRecommended = (i) => setForm((f) => ({ ...f, options: f.options.map((o, idx) => ({ ...o, recommended: idx === i })) }));

  const addOption = () => {
    if (form.options.length >= 4) return toast.error("Maximum 4 options");
    setForm((f) => ({ ...f, options: [...f.options, newOption(`Option ${f.options.length + 1}`, settings?.quote_expiry?.default_days || 15)] }));
    setActive(form.options.length);
  };
  const removeOption = (i) => {
    if (form.options.length <= 1) return toast.error("At least one option required");
    setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));
    setActive((a) => Math.max(0, a >= i ? a - 1 : a));
  };

  const uploadRoof = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setOpt("roof_image_id", data.id); setOpt("roof_view", { zoom: 1, x: 0, y: 0 }); toast.success("Roof image uploaded for this option");
  };
  const copyRoofFrom = (idx) => {
    const src = form.options[idx];
    if (!src) return;
    setForm((f) => ({ ...f, options: f.options.map((o, i) => i === active ? { ...o, roof_image_id: src.roof_image_id || null, roof_layout: src.roof_layout ? JSON.parse(JSON.stringify(src.roof_layout)) : null, roof_view: src.roof_view ? { ...src.roof_view } : null } : o) }));
    setRoofMode(src.roof_layout?.center ? "satellite" : (src.roof_image_id ? "upload" : "satellite"));
    toast.success(`Roof layout copied from ${src.label || `Option ${idx + 1}`}`);
  };

  const buildPayload = () => ({
    lead_id: form.lead_id,
    annual_bill: form.annual_bill === "" ? null : +form.annual_bill,
    roof_image_id: form.roof_image_id, roof_layout: form.roof_layout, notes: form.notes,
    options: form.options.map((o) => ({
      ...o, panel_qty: +o.panel_qty, inverter_qty: +o.inverter_qty, battery_qty: +o.battery_qty,
      out_of_pocket: +o.out_of_pocket, daily_consumption: +o.daily_consumption,
      strategic_discount: +o.strategic_discount, deposit: +o.deposit,
      plenti_term_months: +o.plenti_term_months,
      finance_provider: o.finance_provider || "",
      finance_amount: +(o.finance_amount || 0),
      finance_term_months: +(o.finance_term_months || o.plenti_term_months || 12),
    })),
  });

  // Live pricing preview — recompute every option as the consultant edits (no save needed)
  useEffect(() => {
    if (!form.lead_id) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/quotes/calc-preview", buildPayload());
        setOptionCalcs(data.option_calcs || []);
      } catch { /* keep last good calc */ }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);


  const save = async () => {
    if (!form.lead_id) return toast.error("Select a lead");
    const payload = buildPayload();
    try {
      let qid = id;
      if (id) { await api.put(`/quotes/${id}`, payload); }
      else { const { data } = await api.post("/quotes", payload); qid = data.id; }
      const full = await api.get(`/quotes/${qid}/full`);
      setOptionCalcs(full.data.option_calcs || []); setSaved(full.data.quote);
      setSavedForm(JSON.stringify(form));
      toast.success("Quote saved");
      if (!id) navigate(`/app/quotes/${qid}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/quote/${saved.public_token}`); toast.success("Public link copied"); };

  const [sending, setSending] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMsg, setShareMsg] = useState("");
  const [shareSubject, setShareSubject] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payAmt, setPayAmt] = useState("");
  const [payUrl, setPayUrl] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const restoreActionsFocus = (event) => {
    event.preventDefault();
    document.querySelector('[data-testid="quote-actions-trigger"]')?.focus();
  };
  const openPayLink = () => {
    const dep = optionCalcs.find((c) => c.recommended)?.deposit || optionCalcs[0]?.deposit || "";
    setPayAmt(dep ? String(dep) : ""); setPayUrl(""); setPayOpen(true);
  };
  const generatePayLink = async () => {
    if (!saved) return;
    setPayBusy(true);
    try {
      const { data } = await api.post(`/quotes/${saved.id}/payment-link`, { amount: +payAmt || null });
      setPayUrl(data.url);
      toast.success(`Payment link created for ${currency(data.amount)}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setPayBusy(false); }
  };
  const copyPayLink = () => { navigator.clipboard.writeText(payUrl); toast.success("Payment link copied"); };
  const openShare = () => {
    const lead = leads.find((l) => l.id === form.lead_id);
    const fn = lead?.first_name ? `Hi ${lead.first_name},` : "Hi,";
    setShareSubject(`Your Solar Proposal ${saved?.quote_no || ""} is ready — Grian Solar Power`.replace(/\s+—/, " —").replace("  ", " "));
    setShareMsg(`${fn}\n\nThank you for your time. As promised, here is your personalised solar proposal. Tap the button below to view your system options, savings and pricing.\n\nFeel free to reach out if you have any questions.`);
    setShareOpen(true);
  };
  const emailToCustomer = async () => {
    if (!saved) return;
    setSending(true);
    try {
      await api.post(`/quotes/${saved.id}/send-email`, { message: shareMsg, subject: shareSubject });
      toast.success("Proposal emailed to the customer");
      setShareOpen(false);
    }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const showPanels = opt.quote_type !== "battery";
  const showBattery = opt.quote_type !== "pv";
  const activeCalc = optionCalcs[active];
  const extraProducts = products.filter((p) => !["panel", "inverter", "battery"].includes(p.category) && p.active !== false);
  const selInverter = inverters.find((p) => p.id === opt.inverter_product_id);
  const compatBatteries = (selInverter && (selInverter.supported_battery_ids || []).length)
    ? batteries.filter((b) => selInverter.supported_battery_ids.includes(b.id))
    : batteries;
  const addExtra = (pid) => { if (!pid) return; setOpt("extras", [...(opt.extras || []), { product_id: pid, qty: 1 }]); };
  const setExtra = (i, k, v) => setOpt("extras", (opt.extras || []).map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const removeExtra = (i) => setOpt("extras", (opt.extras || []).filter((_, idx) => idx !== i));

  const switchRoofMode = (mode) => {
    setRoofMode(mode);
    const l = opt.roof_layout;
    if (mode === "upload" && l && (l.center || l.panelWf !== undefined)) setOpt("roof_layout", null);
    if (mode === "satellite" && l && l.scale !== undefined) { setOpt("roof_layout", null); setOpt("roof_image_id", null); }
  };

  const unsavedChanges = savedForm !== null && JSON.stringify(form) !== savedForm;
  const pdfItems = proposalPdfs.items.map((item) => ({ ...item, onSelect: () => {
    if (unsavedChanges) { toast.error("Save Quote before generating or downloading the updated proposal PDF."); return; }
    item.onSelect?.();
  } }));

  return (
    <div className="space-y-6 min-w-0">
      <button data-testid="quote-back" onClick={() => navigate(form.lead_id ? `/app/leads/${form.lead_id}` : "/app/quotes")} className="text-sm text-muted-foreground flex items-center gap-1"><Icons.ChevronLeft size={16} /> {form.lead_id ? "Back to lead" : "Quotes"}</button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-head font-extrabold tracking-tight">{id ? `Edit ${saved?.quote_no || ""}` : "New Quote"}</h1>
        <div className="flex gap-2">
          {saved && <ActionsMenu id="quote-actions" items={[
            { id: "quote-preview", icon: "Eye", label: "Preview Quote", onSelect: () => window.open(`/quote/${saved.public_token}?preview=1`, "_blank") },
            ...pdfItems,
            { id: "email-customer", icon: "Send", label: "Share quote by email", disabled: sending, onSelect: openShare },
            { id: "copy-quote-link", icon: "Link2", label: "Copy link", onSelect: copyLink },
            { id: "payment-link-btn", icon: "CreditCard", label: "Payment link", onSelect: openPayLink },
            ...(["super_admin", "admin", "admin_manager"].includes(user?.role) ? [{ id: "quote-move-recycle", icon: "Trash2", label: "Move to recycle bin", onSelect: async () => {
              if (!window.confirm(`Move ${saved.quote_no} to the recycle bin? Its public link will stop working. Payments and invoices are retained.`)) return;
              try { await api.delete(`/quotes/${saved.id}`); toast.success("Quote moved to recycle bin"); navigate("/app/quotes"); } catch (e) { toast.error(errMsg(e)); }
            } }] : []),
          ]} />}
          <button onClick={save} data-testid="save-quote" className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">Save Quote</button>
        </div>
      </div>

      {saved && <ProposalPdfProgress pdfs={proposalPdfs} leadId={saved.lead_id} />}
      {saved?.signed && <SigningEmailStatus quote={saved} />}
      {saved && unsavedChanges && <p className="text-sm text-amber-600" data-testid="proposal-pdf-unsaved-warning">Unsaved changes — save your quote before generating or downloading updated proposal PDFs.</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* SETUP */}
          <div className="bg-card border border-border rounded-md p-6 shadow-sm">
            <h3 className="font-head font-semibold mb-4">Quote Setup</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label-caps">Lead / Customer</label>
                <select value={form.lead_id} onChange={(e) => setField("lead_id", e.target.value)} data-testid="quote-lead"
                  className="w-full min-w-0 max-w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">— Select lead —</option>
                  {leads.map((l) => <option key={l.id} value={l.id}>{`${l.uid} · ${l.first_name} ${l.last_name} (${l.state})`}</option>)}</select></div>
              <div><label className="label-caps">Customer Annual Electricity Bill (AUD)</label>
                <input type="number" value={form.annual_bill} onChange={(e) => setField("annual_bill", e.target.value)} data-testid="quote-annual-bill"
                  placeholder={`Default ${currency(settings?.default_annual_bill || 2400)}`}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                <div className="text-[11px] text-muted-foreground mt-0.5">Used to estimate savings. Left blank → uses the default average.</div></div>
            </div>
          </div>

          {/* OPTION TABS */}
          <div className="bg-card border border-border rounded-md p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {form.options.map((o, i) => (
                <div key={o.id} className={`max-w-full min-w-0 rounded-full text-sm font-semibold flex items-center transition-colors ${active === i ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  <button onClick={() => setActive(i)} data-testid={`option-tab-${i}`} className="min-w-0 px-3 py-1.5 text-left [overflow-wrap:anywhere]">
                    {`Q${i + 1} · ${o.label || "Option"}`}
                  </button>
                  {form.options.length > 1 && (
                    <button type="button" onClick={() => removeOption(i)} data-testid={`remove-option-${i}`} title={`Remove option ${i + 1}`} aria-label={`Remove option ${i + 1}`}
                      className="shrink-0 mr-2 p-1 opacity-60 hover:opacity-100 transition-opacity"><Icons.X size={13} /></button>
                  )}
                </div>
              ))}
              {form.options.length < 4 && (
                <button onClick={addOption} data-testid="add-option" className="px-3 py-1.5 rounded-full text-sm font-semibold border border-dashed border-border text-muted-foreground flex items-center gap-1.5"><Icons.Plus size={14} /> Add Option</button>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div><label className="label-caps">Option Name</label>
                <input value={opt.label} onChange={(e) => setOpt("label", e.target.value)} data-testid="option-label"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              <div><label className="label-caps">Quote Type</label>
                <select value={opt.quote_type} onChange={(e) => {
                  const type = e.target.value;
                  setForm((previous) => ({ ...previous, options: previous.options.map((option, index) => index === active ? {
                    ...option, quote_type: type, ...(type === "battery" ? { incentives: { ...(option.incentives || {}), stc: false, vic_rebate: false, vic_loan: false } } : {})
                  } : option) }));
                }} data-testid="quote-type"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  {QUOTE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer self-center">
                <input type="checkbox" checked={!!opt.recommended} onChange={() => setRecommended(active)} data-testid="option-recommended" />
                Mark as ⭐ Recommended (shown to customer)
              </label>
              <div><label className="label-caps">Proposal notes (shown on this option)</label>
                <textarea value={opt.notes} onChange={(e) => setOpt("notes", e.target.value)} rows={2} data-testid="option-notes"
                  placeholder="Any extra words to show the customer for this option…"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" /></div>
            </div>

            <div className="mb-4 max-w-xs">
              <label className="label-caps" htmlFor="option-expires-on">Expiry date</label>
              <input id="option-expires-on" type="date" value={opt.expires_on || ""} onChange={(e) => setOpt("expires_on", e.target.value)}
                data-testid="option-expires-on" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
            </div>

            <div className="mb-4">
              <label className="label-caps">Installation Notes (internal — Job Pack only)</label>
              <textarea value={opt.installation_notes || ""} onChange={(e) => setOpt("installation_notes", e.target.value)} rows={2} data-testid="option-installation-notes"
                placeholder="Notes for the installer — NOT shown to the customer, appears only on the Job Pack…"
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
            </div>

            {/* PRODUCTS */}
            <div className="space-y-4 border-t border-border pt-4">
              <h4 className="font-semibold text-sm">Products</h4>
              {showPanels && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div className="sm:col-span-2 min-w-0"><label className="label-caps">Solar Panel</label>
                    <SearchSelect testid="quote-panel" value={opt.panel_product_id} onChange={(v) => setOpt("panel_product_id", v)}
                      options={panels} placeholder="— Select panel —"
                      getLabel={(p) => `${p.brand} ${p.model_no}${p.watts ? ` (${p.watts}W)` : ""}${p.series ? ` · ${p.series}` : ""}`} /></div>
                  <div><label className="label-caps">Qty</label>
                    <input type="number" value={opt.panel_qty} onChange={(e) => setOpt("panel_qty", e.target.value)} data-testid="quote-panel-qty"
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="col-span-2 min-w-0"><label className="label-caps">Inverter</label>
                  <SearchSelect testid="quote-inverter" value={opt.inverter_product_id} onChange={(v) => { setOpt("inverter_product_id", v); const inv = inverters.find((x) => x.id === v); if (inv && (inv.supported_battery_ids || []).length && opt.battery_product_id && !inv.supported_battery_ids.includes(opt.battery_product_id)) setOpt("battery_product_id", ""); }}
                    options={inverters} placeholder="— Select inverter —"
                    getLabel={(p) => `${p.brand} ${p.model_no}${p.size_kw ? ` · ${p.size_kw}kW` : ""}${p.phase ? ` · ${p.phase === "three" ? "3-phase" : "1-phase"}` : ""}`} /></div>
                <div><label className="label-caps">Qty</label>
                  <input type="number" value={opt.inverter_qty} onChange={(e) => setOpt("inverter_qty", e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              </div>
              {showBattery && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div className="sm:col-span-2 min-w-0"><label className="label-caps">Battery {selInverter && compatBatteries.length !== batteries.length && <span className="text-muted-foreground normal-case">(compatible with selected inverter)</span>}</label>
                    <SearchSelect testid="quote-battery" value={opt.battery_product_id} onChange={(v) => setOpt("battery_product_id", v)}
                      options={compatBatteries} placeholder={compatBatteries.length ? "— Select battery —" : "No compatible batteries"}
                      getLabel={(p) => `${p.brand} ${p.model_no}${p.usable_capacity ? ` (${p.usable_capacity}kWh usable)` : ""}${p.series ? ` · ${p.series}` : ""}`} /></div>
                  <div><label className="label-caps">Qty</label>
                    <input type="number" value={opt.battery_qty} onChange={(e) => setOpt("battery_qty", e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
                </div>
              )}
              {/* Additional components (gateway, meter, etc.) */}
              <div className="border-t border-border pt-4">
                <label className="label-caps">Additional Components</label>
                <select value="" onChange={(e) => { addExtra(e.target.value); e.target.value = ""; }} data-testid="add-extra"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">+ Add a component (gateway, meter, accessory…)</option>
                  {extraProducts.map((p) => <option key={p.id} value={p.id}>{`${p.brand} ${p.model_no}`}</option>)}
                </select>
                <div className="mt-2 space-y-2">
                  {(opt.extras || []).map((e, i) => {
                    const p = products.find((x) => x.id === e.product_id);
                    return (
                      <div key={i} className="flex items-center gap-2 bg-muted/30 rounded-md p-2" data-testid={`extra-row-${i}`}>
                        <span className="flex-1 text-sm">{p ? `${p.brand} ${p.model_no}` : "Unknown"}</span>
                        <input type="number" value={e.qty} onChange={(ev) => setExtra(i, "qty", +ev.target.value)} className="w-16 px-2 py-1 rounded border border-input bg-background text-sm" />
                        <button onClick={() => removeExtra(i)} className="text-destructive"><Icons.X size={15} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="space-y-4 border-t border-border pt-4 mt-4">
              <div>
                <h4 className="font-semibold text-sm flex items-center gap-1.5"><Icons.BadgePercent size={16} className="text-primary" /> Applicable Incentives</h4>
                <p className="text-[11px] text-muted-foreground mt-0.5">Tick the incentives/rebates that apply to this customer. These flow into the proposal &amp; tax invoice.</p>
              </div>
              {(() => {
                const inc = opt.incentives || {};
                const incVal = (k) => (inc[k] !== undefined ? inc[k] : true);
                const setInc = (k, v) => setOpt("incentives", { stc: incVal("stc"), battery_stc: incVal("battery_stc"), vic_rebate: incVal("vic_rebate"), vic_loan: incVal("vic_loan"), [k]: v });
                const items = [
                  ["stc", "Solar STC (small-scale certificates)", showPanels],
                  ["battery_stc", "Battery STC (Cheaper Home Batteries)", showBattery],
                  ["vic_rebate", "Solar Victoria Rebate", showPanels],
                  ["vic_loan", "Solar Victoria Interest-Free Loan", showPanels],
                ];
                return (
                  <div className="grid sm:grid-cols-2 gap-2">
                    {items.filter(([, , show]) => show).map(([k, l]) => (
                      <label key={k} className="flex items-center gap-2 text-sm cursor-pointer bg-muted/30 rounded-md px-3 py-2">
                        <input type="checkbox" checked={incVal(k)} onChange={(e) => setInc(k, e.target.checked)} data-testid={`incentive-${k}`} />
                        {l}
                      </label>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="space-y-4 border-t border-border pt-4 mt-4">
              <h4 className="font-semibold text-sm">Pricing</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="label-caps">Out of Pocket (AUD)</label>
                  <input type="number" value={opt.out_of_pocket} onChange={(e) => setOpt("out_of_pocket", e.target.value)} data-testid="quote-oop"
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                  <div className="text-[11px] text-muted-foreground mt-0.5">Price after all government rebates.</div></div>
                <div><label className="label-caps">Strategic Discount (AUD)</label>
                  <input type="number" value={opt.strategic_discount} onChange={(e) => setOpt("strategic_discount", e.target.value)} data-testid="quote-discount"
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                  <div className="text-[11px] text-muted-foreground mt-0.5">Subtracted from final price on the proposal.</div></div>
                <div><label className="label-caps">Deposit (AUD)</label>
                  <input type="number" value={opt.deposit} onChange={(e) => setOpt("deposit", e.target.value)} data-testid="quote-deposit"
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
                <div><label className="label-caps">Daily Consumption (kWh)</label>
                  <input type="number" value={opt.daily_consumption} onChange={(e) => setOpt("daily_consumption", e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              </div>
            </div>

            {/* FINANCE (Plenti / Brighte) */}
            <div className="space-y-3 border-t border-border pt-4 mt-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="label-caps">Finance Provider</label>
                  <select value={opt.finance_provider || ""} onChange={(e) => setOpt("finance_provider", e.target.value)} data-testid="finance-provider"
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                    <option value="">No finance</option>
                    <option value="plenti">Plenti</option>
                    <option value="brighte">Brighte</option>
                  </select></div>
                {opt.finance_provider && (
                  <div><label className="label-caps">Finance Term</label>
                    <select value={opt.finance_term_months || opt.plenti_term_months} onChange={(e) => setOpt("finance_term_months", e.target.value)} data-testid="finance-term"
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                      {(settings?.[opt.finance_provider]?.terms || plentiTerms).map((t) => <option key={t.months} value={t.months}>{`${t.months} months (${t.percent}%)`}</option>)}</select></div>
                )}
              </div>
              {opt.finance_provider && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="label-caps">Amount Financed (AUD)</label>
                    <input type="number" step="0.01" value={opt.finance_amount || ""} onChange={(e) => setOpt("finance_amount", e.target.value)} data-testid="finance-amount"
                      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="Blank = finance the whole balance" />
                    <div className="text-[11px] text-muted-foreground mt-0.5">Rest is paid in cash. Leave blank to finance the full balance.</div></div>
                  {activeCalc?.finance && (
                    <div className="bg-secondary/10 rounded-md p-3 text-sm self-end">
                      <div className="flex justify-between"><span className="text-muted-foreground">Financed</span><b>{currency(activeCalc.finance.financed)}</b></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Est. monthly</span><b>{currency(activeCalc.finance.monthly_repayment)}/mo</b></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ROOF */}
          <div className="bg-card border border-border rounded-md p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-head font-semibold min-w-0 [overflow-wrap:anywhere]">Roof Layout Design <span className="text-xs font-normal text-muted-foreground">(for {opt.label || `Option ${active + 1}`} only)</span></h3>
              <div className="flex items-center gap-2 flex-wrap min-w-0 max-w-full">
              {form.options.length > 1 && (
                <select onChange={(e) => { if (e.target.value !== "") copyRoofFrom(+e.target.value); e.target.value = ""; }} defaultValue="" data-testid="roof-copy-from"
                  className="min-w-0 max-w-full w-full sm:w-auto sm:max-w-xs px-2 py-1.5 rounded-md border border-border bg-card text-sm">
                  <option value="">Copy roof layout from…</option>
                  {form.options.map((o, i) => i !== active && <option key={o.id} value={i}>{o.label || `Option ${i + 1}`}</option>)}
                </select>
              )}
              <div className="flex rounded-md border border-border overflow-hidden text-sm">
                <button type="button" onClick={() => switchRoofMode("satellite")} data-testid="roof-mode-satellite"
                  className={`px-3 py-1.5 font-semibold ${roofMode === "satellite" ? "bg-primary text-primary-foreground" : "bg-card"}`}>Google Satellite</button>
                <button type="button" onClick={() => switchRoofMode("upload")} data-testid="roof-mode-upload"
                  className={`px-3 py-1.5 font-semibold ${roofMode === "upload" ? "bg-primary text-primary-foreground" : "bg-card"}`}>Upload Image</button>
              </div>
              </div>
            </div>
            {roofMode === "satellite" ? (
              <GoogleRoofDesigner
                lead={leads.find((l) => l.id === form.lead_id)}
                key={opt.id}
                value={opt.roof_layout}
                panel={panels.find((p) => p.id === opt.panel_product_id)}
                settings={settings}
                dailyConsumption={+opt.daily_consumption || 20}
                onChange={(l) => { setOpt("roof_layout", l); if (showPanels) setOpt("panel_qty", (l.panels || []).length); }}
              />
            ) : (
              <>
                <label className="text-sm px-3 py-1.5 rounded-md border border-border cursor-pointer inline-flex items-center gap-1.5 mb-3">
                  <Icons.Upload size={14} /> Upload Roof Image
                  <input ref={roofRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadRoof(e.target.files[0])} data-testid="roof-upload" />
                </label>
                <RoofDesigner key={opt.id} imageId={opt.roof_image_id} layout={opt.roof_layout} onChange={(l) => setOpt("roof_layout", l)}
                  view={opt.roof_view} onViewChange={(v) => setOpt("roof_view", v)} />
              </>
            )}
          </div>
        </div>

        {/* LIVE CALC */}
        <div className="space-y-4 min-w-0">
          <div className="bg-card border border-border rounded-md p-6 shadow-sm lg:sticky lg:top-4" data-testid="calc-panel">
            <h3 className="font-head font-semibold mb-1 flex items-center gap-2">Pricing Summary
              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Live</span></h3>
            <p className="text-[11px] text-muted-foreground mb-4">Updates automatically as you edit — no need to save first.</p>
            {!optionCalcs.length && <p className="text-sm text-muted-foreground">Add products &amp; pricing to see live totals.</p>}
            {optionCalcs.map((c, i) => (
              <div key={c.option_id} className={`mb-4 pb-4 ${i < optionCalcs.length - 1 ? "border-b border-border" : ""}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-semibold min-w-0 [overflow-wrap:anywhere]" data-testid={`calc-option-label-${i}`}>{`Q${i + 1} · ${c.option_label}`}</span>
                  <span className="text-xs text-muted-foreground shrink-0" data-testid={`calc-option-capacity-${i}`}>{c.system_kw ? `${c.system_kw} kW` : ""}{c.usable_capacity ? ` · ${c.usable_capacity} kWh` : ""}</span>
                </div>
                <Row l="Total Rebates" v={currency(c.total_rebates)} />
                {c.strategic_discount > 0 && <Row l="Strategic Discount" v={`− ${currency(c.strategic_discount)}`} />}
                {c.deposit > 0 && <Row l="Deposit" v={currency(c.deposit)} />}
                <div className="bg-secondary/10 rounded-md p-2 mt-1">
                  <div className="label-caps">Final Price</div>
                  <div className="text-xl font-head font-extrabold text-secondary">{currency(c.final_price)}</div>
                </div>
                {c.show_energy && <div className="text-xs text-muted-foreground pt-1">1st-yr saving {currency(c.performance.annual_saving)} · 25-yr {currency(c.performance.savings_25yr)}</div>}
                {c.finance && <div className="text-xs text-muted-foreground">{c.finance.provider_label} {c.finance.term_months}mo · {currency(c.finance.monthly_repayment)}/mo</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={shareOpen} onOpenChange={(open) => { if (!sending) setShareOpen(open); }}>
          <DialogContent onCloseAutoFocus={restoreActionsFocus} className="bg-card rounded-md w-[calc(100vw_-_2rem)] max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-lg block" data-testid="share-quote-modal">
            <DialogTitle className="text-lg font-head font-extrabold mb-1 pr-5">Share proposal by email</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mb-4">Edit the subject and message below. It&apos;s sent from your own mailbox and placed inside the branded Grian email with the proposal button automatically. Just write plain text — no HTML needed.</DialogDescription>
            <label className="label-caps">Subject line</label>
            <input value={shareSubject} onChange={(e) => setShareSubject(e.target.value)} data-testid="share-quote-subject"
              className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-input bg-background text-sm" placeholder="Email subject" />
            <label className="label-caps">Message to customer</label>
            <textarea value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} rows={8} data-testid="share-quote-message"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm leading-relaxed" />
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setShareOpen(false)} data-testid="share-quote-cancel" className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button onClick={emailToCustomer} disabled={sending} data-testid="share-quote-send"
                className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold flex items-center gap-1.5 disabled:opacity-50">
                <Icons.Send size={15} /> {sending ? "Sending…" : "Send Email"}</button>
            </div>
          </DialogContent>
      </Dialog>
      <Dialog open={payOpen} onOpenChange={(open) => { if (!payBusy) setPayOpen(open); }}>
          <DialogContent onCloseAutoFocus={restoreActionsFocus} className="bg-card rounded-md w-[calc(100vw_-_2rem)] max-w-md max-h-[90vh] overflow-y-auto p-6 shadow-lg block" data-testid="payment-link-modal">
            <DialogTitle className="text-lg font-head font-extrabold mb-1 pr-5">Generate payment link</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mb-4">Creates a secure Stripe checkout link for the customer's deposit. Copy it and send via email or SMS. Links are valid for 24 hours.</DialogDescription>
            <label className="label-caps">Amount (AUD)</label>
            <input type="number" min="1" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} data-testid="payment-link-amount"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="e.g. 500" />
            {payUrl && (
              <div className="mt-4 bg-muted/40 border border-border rounded-md p-3" data-testid="payment-link-result">
                <div className="text-xs font-semibold text-primary mb-1 flex items-center gap-1"><Icons.CheckCircle2 size={13} /> Link ready</div>
                <div className="text-xs text-muted-foreground break-all">{payUrl}</div>
                <button onClick={copyPayLink} data-testid="payment-link-copy" className="mt-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-1"><Icons.Copy size={13} /> Copy link</button>
              </div>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setPayOpen(false)} data-testid="payment-link-close" className="px-4 py-2 rounded-md border border-border">Close</button>
              <button onClick={generatePayLink} disabled={payBusy || !(+payAmt > 0)} data-testid="payment-link-generate"
                className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold flex items-center gap-1.5 disabled:opacity-50">
                <Icons.CreditCard size={15} /> {payBusy ? "Creating…" : "Generate Link"}</button>
            </div>
          </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span>{v}</span></div>
);
